import { Source } from '../../enums/enum.source';
import { ItemDeletedResponse } from '../../responses/item.delete.response';
import { ContactEntity } from '../contact/contact.options';
import { ContactType } from '../contact/contact.type';
import { Entity, EntityLink, TimestampDetails } from '../entity';
import { Job } from '../job/job';
import { WorkOrder } from '../job/workorder';
import { WorkOrderStatus } from '../job/workorder.status';
import { TaskApprovalStatus } from '../planned.work/visit/task.approval';
import type { VisitSpacingFacts } from '../planned.work/visit/visit.spacing';
import { Address } from '../shared/address';
import { Lex } from '../shared/lexorank';
import { Location } from '../shared/location';
import { SimpleGoogleRoute } from '../shared/simple.google.route';
import { SiteTiny } from '../shared/site';
import { AppointmentPro } from './appointment.pro';

export function getJobEntity(job: Job) {
    if (!job) return null;
    return { _id: job._id, name: job.jobNumber } as Entity;
}

export function getWorkOrderEntity(job: WorkOrder) {
    if (!job) return null;
    return { _id: job._id, name: job.workOrderNumber } as Entity;
}

export const UnallocatedUserId = '00000000-0000-0000-0000-1000000000';

/** Visible label. Stored `name` is `<Unassigned>`, which HTML views treat as a tag. */
export const UnallocatedUserLabel = 'Unassigned';

export const UnallocatedUser: ContactEntity = {
    _id: UnallocatedUserId,
    name: '<Unassigned>',
    type: "System" as any
};

export function isUnallocatedUser(value: { _id?: string } | string | null | undefined): boolean {

    const id = typeof value === 'string' ? value : value?._id;
    return id === UnallocatedUserId;
}

export enum AllocationStatus {
    Open = 'Open',
    Enroute = 'Enroute',
    OnSite = 'Onsite',
    CheckedOut = 'Checked Out',
    Completed = 'Completed',
    Cancelled = 'Cancelled',
    Overhead = 'Overhead'
}

export const AllocationStatuses = [
    AllocationStatus.Open,
    AllocationStatus.Enroute,
    AllocationStatus.OnSite,
    AllocationStatus.CheckedOut,
    AllocationStatus.Completed,
    AllocationStatus.Cancelled,
    AllocationStatus.Overhead
];

export enum AllocationType {
    PhoneCall = 'Phone Call',
    Meeting = 'Meeting',
    Onsite = 'Onsite'
}

export enum BookingStatus {
    Confirmed = 'Confirmed',
    Tentative = 'Tentative'
}

export enum AllocationReminderStatus {
    NotRequired = 'Not Required',
    Pending = 'Pending',
    Sent = 'Sent'
}

export type RunLifeCycle = 'Pre' | 'Post' | 'None';


export const AllocationBookingStatus = [BookingStatus.Confirmed, BookingStatus.Tentative];

export type AllocationStatusInfo = {
    reason?: string;
    on?: Date;
    by?: Entity;
    note?: string;
}

export type AllocationRule = {

    /** rule string, eg: RRULE:FREQ=WEEKLY;COUNT=30;INTERVAL=1;WKST=MO */
    _id: string;

    /** rule text, eg: every week for 30 times */
    name: string;

    start?: Date;
    end?: Date;
    exclude?: Date[];
}

export interface Allocation extends Entity, TimestampDetails {
    _id: string;
    active?: boolean;
    run: Entity;
    runOrder?: Lex;
    resource: ContactEntity;
    startDate: Date;
    endDate: Date;
    status: AllocationStatus;

    booking?: Entity;
    job?: Entity;
    workOrder?: Entity;

    opportunity?: Entity;

    site?: SiteTiny;
    overhead?: AppointmentPro;
    clusterColor?: string;
    note?: string;
    type?: AllocationType;

    dateCompleted?: Date;
    completedNote?: string;

    allocations?: OtherAllocation[];

    createdAt?: Date;
    updatedAt?: Date;
    createdBy?: Entity;
    updatedBy?: Entity;
    contractorResource?: ContactEntity;
    source: Source;

    resourceMigration?: any;
    notificationSent?: boolean;

    additionalDays?: number;
    bookingStatus?: BookingStatus;

    locked?: boolean;
    allDay?: boolean;

    /** TODO: [Question] This field isn't saved to DB, what it for? */
    timezone?: string;

    // handle create allocation for equipment
    groupId?: string;

    // Route Information
    estimatedDuration?: number;
    travelDistance?: number;
    travelDuration?: number;
    travelDistanceString?: string;
    travelDurationString?: string;

    policy?: Entity;
    reminderStatus?: AllocationReminderStatus;

    statusInfo?: AllocationStatusInfo;

    rule?: AllocationRule;
    parentId?: string;

    integrationId?: string;


    /** Not save to db: 
     * calculation field for displaying, 
     * if resource.scheduling.tags not exactly equal allocation.workOrder.schedulingTags */
    isConflict?: boolean;

    // Not save to db: 
    // This field is a flag to indicate that the allocation is created by selecting multiple slots from the calendar.
    selectedMultipleSlots?: boolean;


    /** @description virtual allocation for some-entity-type */
    virtualEntity?: EntityLink;

    /**
     * The surviving allocation this Visit card was merged into when its Visit joined a combined Job
     * (WBS-11807). Kept rather than deleted so the Visit stays traceable to its booking; see `ALLOCATION_NOT_MERGED_MATCH`.
     */
    mergedInto?: string;
}

export interface AllocationWithMeta extends Allocation {
    meta: AllocationMeta;
}

export interface AllocationMeta {
    name?: string;
    type: Entity;
    priority: Entity;
    tags: Entity[];
    jobTags?: Entity[];
    schedulingTags?: Entity[];
    customer: string;
    customerWO: string;
    category: Entity;
    status?: WorkOrderStatus;
    site: {
        _id: string;
        address: string;
        contactName: string;
        contactPhone: string;
        contactWorkPhone?: string;
        contactMobile?: string;
    };
    dueDate?: Date;
    /**
     * The Service Plan a Visit-backed card belongs to. Absent on every other kind of card.
     *
     * Carried on the card because the deep-link target for a planned Visit is its plan's manage page,
     * and the preview component must be able to build that URL without a second round trip (WBS-11002).
     * A dedicated Visit surface is a later ticket; when it lands, this becomes the link's fallback rather
     * than its destination.
     */
    servicePlanId?: string;
    /** Present only on a Visit-backed card, so the card can hide Job-only fields instead of showing "Not Set". */
    visit?: AllocationVisitMeta;

}

/** What a Visit-backed card shows until its Job exists; after that the card is a Job card again. */
export interface AllocationVisitMeta {
    servicePlan: Entity;
    serviceLine?: Entity;
    /** The minted Job number, when one has been reserved; never the Visit id. */
    number?: string;
    localDate?: string;
    /** Section 15 compliance window, local dates; present only when a due task carries leeway. */
    windowStart?: string;
    windowEnd?: string;
    timezone?: string;
    approvalStatus?: TaskApprovalStatus;
    /** Section 16: the spaced tasks' neighbours, so a card move can warn before it breaks spacing. */
    spacing?: VisitSpacingFacts[];
}

export interface AllocationCard extends Pick<
    Allocation,
    '_id'
    | 'resource'
    | 'startDate'
    | 'endDate'
    | 'status'
    | 'contractorResource'
    | 'overhead'
    | 'job'
    | 'workOrder'
    | 'opportunity'
    | 'run'
    | 'source'
    | 'note'
    | 'bookingStatus'
    | 'groupId'
    | 'locked'
    | 'rule'
    | 'parentId'
    | 'allDay'
    | 'isConflict'
    | 'virtualEntity'
> {
    meta?: AllocationMeta;
    metaOpportunity?: AllocationMeta;
    site?: SiteTiny;
}

export interface BasicAllocation {
    resource: string,
    startDate: string,
    endDate: string,
    note: string
}


export interface OtherAllocation {
    _id: string;
    resource: Entity;
    status: AllocationStatus;
    startDate: Date;
}

export interface AllocationSummary {
    _id: string;
    startDate: Date;
    endDate: Date;
    resource: Entity;
    canDelete?: boolean;
    status: AllocationStatus;
}

export interface AllocationPatch extends Partial<Pick<
    Allocation,
    '_id'
    | 'resource'
    | 'contractorResource'
    | 'startDate'
    | 'endDate'
    | 'note'
    | 'status'
    | 'updatedBy'
    | 'source'
    | 'notificationSent'
    | 'type'
    | 'bookingStatus'
    | 'locked'
    | 'allDay'
    | 'statusInfo'
    | 'rule'
    | 'overhead'
    | 'integrationId'
>> {
}


export interface AllocationBatchRequest {
    deleted: string[];
    modified: AllocationPatch[];
    new: Allocation[];
    portal?: boolean;
    runLifeCycle?: RunLifeCycle;
    canSendNotification?: boolean;
}

export interface AllocationBatchResponse {
    deleted: ItemDeletedResponse[];
    modified: Allocation[];
    new: Allocation[];
    success: boolean;
    error?: string;
}

export interface AllocationTrackingContact {
    _id: string;
    name: string;
    phone?: string;
    mobile?: string;
    location: Location;
}

export interface AllocationTrackable {
    trackable: boolean;
    expired?: boolean;
    refresh?: boolean;
}

export interface AllocationTracking extends AllocationTrackable {
    estTimeMinutes: number;
    estDistance: string;
    jobNumber: string;
    workOrderNumber: string;
    customerWO: string;
    address: Address;
    resource: AllocationTrackingContact;
    message: string;
    status: AllocationStatus;
    logo?: string;
    route?: SimpleGoogleRoute;
}
